import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import type { ReactNode } from 'react';

import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import useCanPublish from '../hooks/useCanPublish';
import DraftPublishManager from './DraftPublishManager';

interface IPublishButtonProps {
  /** The automation/draft ID */
  automationId: string;
  /** Button variant - minimal shows only icon, default shows primary button with text */
  variant?: 'minimal' | 'default';
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Optional id for the button element */
  id?: string;
  /** Tour target identifier for product tours */
  'data-tour-id'?: string;
  /** Notifies containers when the publish dialog opens or closes. */
  onDialogOpenChange?: (open: boolean) => void;
  /**
   * Renders a custom trigger while this component keeps the publish dialog
   * mounted outside transient containers such as dropdown-menu content.
   */
  renderTrigger?: (props: IPublishTriggerRenderProps) => ReactNode;
}

interface IPublishTriggerRenderProps {
  disabled: boolean;
  isLoading: boolean;
  onPublish: () => void;
}

/**
 * PublishButton component for publishing draft automations.
 *
 * Enables the button once the latest draft version is known and manages
 * DraftPublishManager dialog state internally.
 *
 * @param {IPublishButtonProps} props - Component props
 * @param {string} props.automationId - The automation/draft ID
 * @param {'minimal' | 'default'} [props.variant='default'] - Button variant style
 * @returns {JSX.Element} Publish button component
 */
export function PublishButton({
  automationId,
  variant = 'default',
  disabled = false,
  id,
  'data-tour-id': tourId,
  onDialogOpenChange,
  renderTrigger,
}: IPublishButtonProps) {
  const { t: tDrafts } = useTranslation('drafts');
  const { t: tCommon } = useTranslation('common');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    value: isPublishDialogOpen,
    setTrue: openPublishDialog,
    setFalse: closePublishDialog,
  } = useBoolean(false);

  const {
    canPublish,
    hasCode,
    isLoading,
    isError,
    draftVersion,
    displayName,
    hasPublishedVersion,
  } = useCanPublish(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    Boolean(automationId && currentOrganizationId && currentWorkspaceId),
  );

  const isDisabled = !canPublish || isLoading || isError || disabled;
  // `canPublish` doesn't cover the ids or the display name, so the trigger can
  // be enabled while the dialog has nothing to mount with (e.g. a draft with a
  // version and code but a blank `display_name`). Anything the dialog needs has
  // to be checked here too, or a container told "open" never hears "closed".
  const publishDialogProps =
    currentOrganizationId && currentWorkspaceId && draftVersion && displayName
      ? { currentOrganizationId, currentWorkspaceId, draftVersion, displayName }
      : undefined;
  const handlePublish = () => {
    if (!publishDialogProps) return;
    openPublishDialog();
    onDialogOpenChange?.(true);
  };
  const handleClosePublishDialog = () => {
    closePublishDialog();
    onDialogOpenChange?.(false);
  };

  const buttonContent = renderTrigger ? (
    renderTrigger({
      disabled: isDisabled,
      isLoading,
      onPublish: handlePublish,
    })
  ) : variant === 'minimal' ? (
    <Button
      id={id}
      data-tour-id={tourId}
      size="icon"
      variant="outline"
      disabled={isDisabled}
      onClick={handlePublish}
      isLoading={isLoading}
      aria-label={tDrafts('drafts.actions.publish')}
    >
      <Icon type="Rocket" size="sm" />
    </Button>
  ) : (
    <Button
      id={id}
      data-tour-id={tourId}
      variant="default"
      size="default"
      disabled={isDisabled}
      onClick={handlePublish}
      isLoading={isLoading}
    >
      <Icon type="Rocket" size="sm" />
      {tDrafts('drafts.actions.publish')}
    </Button>
  );

  const getTooltipText = () => {
    if (isError && !isLoading) {
      return tCommon('common.error.unknown');
    }
    if (isDisabled && !isLoading) {
      // Nothing to publish yet — the draft carries no SPy code, which the
      // publish endpoints reject outright.
      if (!hasCode) {
        return tDrafts('drafts.actions.publish.disabled.noCode');
      }
      // If draft was created from a published automation, show pending changes message
      if (hasPublishedVersion) {
        return tDrafts('drafts.actions.publish.disabled.pendingChanges');
      }
      // Otherwise show the generic "needs a successful run" message
      return tDrafts('drafts.actions.publish.disabled');
    }
    return tDrafts('drafts.actions.publish');
  };

  const renderButton = () => {
    return (
      <Tooltip delayDuration={200} variant="primary">
        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
        <TooltipContent>{getTooltipText()}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <>
      {renderTrigger ? buttonContent : renderButton()}
      {publishDialogProps && (
        <DraftPublishManager
          isOpen={isPublishDialogOpen}
          onClose={handleClosePublishDialog}
          draftId={automationId}
          {...publishDialogProps}
        />
      )}
    </>
  );
}
